import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Receipt, Loader2 } from "lucide-react";
import { formatINR } from "@/lib/format";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { createPdfDoc, withDefaults, drawTotalsBlock, finalizePdf, fmtINR } from "@/lib/pdf";

const months = () => {
  const arr = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push({
      value: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`,
      label: d.toLocaleString("en-IN", { month: "long", year: "numeric" }),
    });
  }
  return arr;
};

export const PayslipDialog = ({ user, open, onOpenChange }) => {
  const monthOpts = months();
  const [month, setMonth] = useState(monthOpts[0].value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users/${user.id}/payslip?month=${month}`);
        setData(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load payslip");
        setData(null);
      } finally { setLoading(false); }
    };
    fetch();
  }, [user, month, open]);

  const downloadPDF = async () => {
    if (!data) return;
    const doc = await createPdfDoc();
    doc.setFontSize(16); doc.setTextColor(225, 29, 42);
    doc.text("Coke@9 OCT — Payslip", 12, 14);
    doc.setFontSize(9); doc.setTextColor(60);
    doc.text(`Powered by BBIPL for CCI`, 12, 20);
    doc.text(`Employee: ${data.user.name} (+91 ${data.user.phone})`, 12, 27);
    doc.text(`Store: ${data.store?.store_name || "—"} (${data.store?.store_code || "—"})`, 12, 32);
    doc.text(`Month: ${data.month} · Working-day reference: ${data.working_days_reference}`, 12, 37);

    autoTable(doc, withDefaults({
      startY: 44,
      head: [["Attendance", "Days"]],
      body: [
        ["Present", data.present],
        ["Late", data.late],
        ["Absent", data.absent],
        ["Days elapsed in month", data.days_elapsed],
      ],
    }));

    autoTable(doc, withDefaults({
      startY: doc.lastAutoTable.finalY + 6,
      head: [["Component", "Amount"]],
      body: [
        ["Monthly salary (reference)", fmtINR(data.user.salary)],
        [`Per-day rate (÷ ${data.working_days_reference})`, fmtINR(data.per_day_rate)],
        ["Gross (days elapsed × per-day)", fmtINR(data.gross)],
        ["Late deduction (25% × late days)", `- ${fmtINR(data.late_deduction)}`],
        ["Absent deduction", `- ${fmtINR(data.absent_deduction)}`],
      ],
      headStyles: { fillColor: [225,29,42], textColor: [255,255,255], font: "NotoSans", fontStyle: "bold" },
    }));

    drawTotalsBlock(doc, [{ label: "Net Payable", value: fmtINR(data.net) }], { title: "Payslip" });

    finalizePdf(doc, { footer: `Generated by Coke@9 OCT - powered By BBIPL · ${new Date().toLocaleString("en-IN")}` });
    doc.save(`payslip_${user.name.replace(/\s+/g,"_")}_${month}.pdf`);
    toast.success("Payslip PDF downloaded");
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payslip-dialog" className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-brand-red" /> Payslip — {user.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Month</label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="payslip-month-select" className="h-12"><SelectValue /></SelectTrigger>
              <SelectContent>
                {monthOpts.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading && <div className="py-8 text-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>}

          {!loading && data && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg bg-green-50 p-3 border border-green-200 text-center">
                  <div className="text-[10px] uppercase font-bold text-green-800">Present</div>
                  <div className="font-display text-2xl font-black text-green-900">{data.present}</div>
                </div>
                <div className="rounded-lg bg-amber-50 p-3 border border-amber-200 text-center">
                  <div className="text-[10px] uppercase font-bold text-amber-800">Late</div>
                  <div className="font-display text-2xl font-black text-amber-900">{data.late}</div>
                </div>
                <div className="rounded-lg bg-red-50 p-3 border border-red-200 text-center">
                  <div className="text-[10px] uppercase font-bold text-red-800">Absent</div>
                  <div className="font-display text-2xl font-black text-red-900">{data.absent}</div>
                </div>
              </div>

              <div className="rounded-xl bg-gray-50 p-4 space-y-1.5 text-sm" data-testid="payslip-breakdown">
                <div className="flex justify-between"><span className="text-gray-600">Monthly salary (ref)</span><span className="font-semibold">{formatINR(data.user.salary)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Per-day rate</span><span className="font-semibold">{formatINR(data.per_day_rate)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Gross ({data.days_elapsed} days)</span><span className="font-semibold">{formatINR(data.gross)}</span></div>
                <div className="flex justify-between text-red-600"><span>Late deduction</span><span>- {formatINR(data.late_deduction)}</span></div>
                <div className="flex justify-between text-red-600"><span>Absent deduction</span><span>- {formatINR(data.absent_deduction)}</span></div>
                <div className="flex justify-between pt-2 mt-2 border-t border-gray-200 text-base">
                  <span className="font-bold text-gray-900">Net payable</span>
                  <span className="font-display font-black text-xl text-brand-red">{formatINR(data.net)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button data-testid="payslip-pdf-btn" onClick={downloadPDF} disabled={!data} className="bg-brand-red hover:bg-[#B31721]">
            <Download className="h-4 w-4 mr-2" /> Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
